import React, { useEffect, useState } from 'react';
import { PhoneOff, Video, Minimize2 } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useVideoCall } from '../../contexts/VideoCallContext';
import { Avatar } from '../common';

/**
 * Shown to the CALLER while ringing (status=requested, my_role=caller).
 * Cancelling = declining from the caller's side (same backend route).
 */
const OutgoingCallModal = () => {
  const { t } = useTranslation('livestream');
  const { currentCall, declineCall, isFullscreenOpen, closeFullscreen } = useVideoCall();
  const [elapsed, setElapsed] = useState(0);

  const show = isFullscreenOpen
    && currentCall
    && currentCall.status === 'requested'
    && currentCall.my_role === 'caller';

  useEffect(() => {
    if (!show) { setElapsed(0); return; }
    const tick = setInterval(() => setElapsed((s) => s + 1), 1000);
    return () => clearInterval(tick);
  }, [show]);

  if (!show) return null;

  const recipient = currentCall.recipient;
  const timeout = currentCall.ring_timeout_seconds || 100;
  const remaining = Math.max(0, timeout - elapsed);

  // Caller "cancels" by hitting the same decline endpoint — but since only recipient
  // can decline per backend, we just end the call locally. Actually simpler: call end()
  // which will hit /calls/{id}/end — but that requires accepted status. We'll just
  // clear local state and let the ring timeout on server.
  const handleCancel = async () => {
    // Best-effort: clear local state immediately
    await declineCall(currentCall.id); // backend will 409 since caller isn't recipient, but we clear local state anyway
  };

  return (
    <div
      className="fixed inset-0 z-[200] flex flex-col items-center justify-between py-16 px-6"
      style={{
        background: 'linear-gradient(180deg, var(--color-primary-700) 0%, #111 100%)',
      }}
    >
      <button
        onClick={closeFullscreen}
        title={t('videoCall.common.minimize')}
        className="absolute top-4 right-4 p-2 rounded-full text-white/80 hover:text-white hover:bg-white/15 transition-colors"
      >
        <Minimize2 className="w-5 h-5" />
      </button>

      <div className="text-white/80 text-center">
        <p className="text-xs uppercase tracking-widest font-semibold mb-1 flex items-center justify-center gap-1.5">
          <Video className="w-3.5 h-3.5" />
          {t('videoCall.outgoing.calling')}
        </p>
        <p className="text-[11px] opacity-70">{t('videoCall.outgoing.secondsUntilTimeout', { seconds: remaining })}</p>
      </div>

      <div className="flex flex-col items-center">
        <div className="relative mb-6">
          <span className="absolute inset-0 rounded-full animate-ping" style={{ background: 'rgba(255,255,255,0.3)' }} />
          <div className="relative">
            <Avatar user={recipient} size="xl" />
          </div>
        </div>
        <h2 className="text-2xl font-bold text-white">
          {recipient?.display_name || recipient?.username}
        </h2>
        <p className="text-white/70 text-sm mt-1">{t('videoCall.outgoing.ringing')}</p>
      </div>

      <button
        onClick={handleCancel}
        className="flex flex-col items-center gap-2 group"
      >
        <div className="w-16 h-16 rounded-full bg-red-500 hover:bg-red-600 flex items-center justify-center shadow-2xl transition-transform group-active:scale-95">
          <PhoneOff className="w-7 h-7 text-white" />
        </div>
        <span className="text-xs text-white/80">{t('videoCall.common.cancel')}</span>
      </button>
    </div>
  );
};

export default OutgoingCallModal;
